import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { select, Action, Store } from '@ngrx/store';
import * as dashboardActions from '../actions/dashboard-actions';
import * as dashboardReducers from '../reducers/dashboard-reducers';
import { ApplicationState } from '../../store/application-store';
import { UserDashboardHttpService } from '../services/user-dashboard-http.service';
import { DashboardItem } from '../models/dashboard-item';
import { ToastrService } from 'ngx-toastr';
import { DashboardHelperService } from '../../common/services/dashboard-helper-service';
import { GlobalErrorHandler } from '../../common/services/global-error-handler.service';
import { withLatestFrom, switchMap, catchError, map, tap, debounceTime } from 'rxjs';

@Injectable()
export class DashboardEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly userDashboardHttpService: UserDashboardHttpService,
    private readonly store: Store<ApplicationState>,
    private readonly errorHandler: GlobalErrorHandler,
    private readonly messageService: ToastrService,
    private readonly dashboardHelper: DashboardHelperService) {
  }

  
  resetDashboardItems: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardActions.ResetDashboardWidgets>(dashboardActions.RESET_DASHBOARD_WIDGETS),
    switchMap((action) => {
      return this.userDashboardHttpService.setDashboardItems([], action.payload).pipe(
        map(res => {
          this.messageService.info('This dashboard has been reset to the system default layout.',
            undefined,
            { timeOut: 0, extendedTimeOut: 0 });
          return new dashboardActions.LoadComplete(res as DashboardItem[]);
        })
      );
    })
  ));

  
  deleteItem: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardActions.DeleteItem>(dashboardActions.DELETE_ITEM),
    withLatestFrom(this.store.pipe(select(dashboardReducers.selectAllDashboardItems))), // may modify when we have multiple dashboards
    switchMap(([action, items]) => {

      let newItems = items.filter(item => item.id !== action.payload.id);
      if (newItems.length === 0) {
        this.messageService.info('No items left on dashboard, default items will be used.', undefined, { timeOut: 0, extendedTimeOut: 0 });
      }
      return this.userDashboardHttpService.setDashboardItems(newItems, action.payload.dashboardName).pipe(
        map(res => {
          return new dashboardActions.LoadComplete(res as DashboardItem[]);
        }),
        catchError(err => {
          return of({ type: 'DELETE_ITEM_ERROR_UNKNOWN_ACTION' });
        }));
    })
  ));

  
  moveItem: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardActions.MoveItem>(dashboardActions.MOVE_ITEM),
    debounceTime(1000),
    withLatestFrom(this.store.pipe(select(dashboardReducers.selectAllDashboardItems))), // may modify when we have multiple dashboards
    switchMap(([action, items]) => {
      return this.userDashboardHttpService.setDashboardItems(items, action.payload.dashboardName).pipe(
        map(res => {
          return { type: 'NO_OP_ACTION' };
        }),
        catchError(err => {
          return of({ type: 'MOVE_ITEM_ERROR' });
        }));
    })
  ));

  
  load$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardActions.Load>(dashboardActions.LOAD),
    switchMap(action => {
      return this.userDashboardHttpService.getDashboardItems(action.payload).pipe(
        map((items: DashboardItem[]) => new dashboardActions.LoadComplete(items)),
        catchError(err => of(new dashboardActions.LoadError(err)))
      );
    })
  ));

  
  loadError$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardActions.LoadError>(dashboardActions.LOAD_ERROR),
    tap((err: any) => {
      this.errorHandler.handleError(err.payload);
    })
  ), { dispatch: false });

  
  addWidget: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardActions.AddWidget>(dashboardActions.ADD_WIDGET),
    withLatestFrom(this.store.pipe(select(dashboardReducers.selectAllDashboardItems))), // may modify when we have multiple dashboards
    switchMap(([action, items]) => {

      const newItem = action.payload;
      this.dashboardHelper.setNewItemAxis(newItem, items);

      const newItems = [...items, newItem];

      return this.userDashboardHttpService.setDashboardItems(newItems, action.payload.dashboardName).pipe(
        map(res => {
          this.messageService.success(`${newItem.title}`, 'Widget Added:');
          return new dashboardActions.LoadComplete(res);
        }),
        catchError(err => {
          this.messageService.error(`${newItem.title}`, 'Failed to Add Widget:');
          return of({ type: 'ADD_ITEM_ERROR' });
        }));
    })
  ));
}
